// ===================================
// ADMIN VOICE SETTINGS - STAGE 8 UPDATED
// src/pages/AdminVoiceSettings.js
// Voice/VAPI configuration management
// Shows VAPI_WEB_VOICE_ENABLED toggle status
// ===================================

import React, { useState, useEffect } from 'react';
import { getVoiceConfig } from '../services/voiceApi';
import './AdminPages.css';

const AdminVoiceSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getVoiceConfig();
      setConfig(data);
    } catch (err) {
      setError('Failed to load voice configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      
      const response = await fetch('/api/voice/config', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.enabled) {
        setTestResult({ success: true, message: 'Voice configuration loaded successfully!' });
      } else {
        setTestResult({ success: false, message: 'VAPI API key not configured' });
      }
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading voice configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div className="header-content">
          <h1>🎙️ Voice Settings</h1>
          <p>Configure voice interview features</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>⚠️</span> {error}
        </div>
      )}

      <div className="admin-grid">
        {/* Connection Status */}
        <div className="admin-card">
          <div className="card-header">
            <h3>📡 Connection Status</h3>
          </div>
          <div className="card-body">
            <div className="status-grid">
              <div className="status-item">
                <span className="status-label">VAPI API Key</span>
                <span className={`status-badge ${config?.enabled ? 'success' : 'error'}`}>
                  {config?.enabled ? '✅ Configured' : '❌ Not Configured'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Web Voice (Browser)</span>
                <span className={`status-badge ${config?.features?.webVoice ? 'success' : 'warning'}`}>
                  {config?.features?.webVoice ? '✅ Available' : '⚠️ Unavailable'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Web Voice (VAPI)</span>
                <span className={`status-badge ${config?.vapiWebVoiceEnabled ? 'success' : 'warning'}`}>
                  {config?.vapiWebVoiceEnabled ? '✅ Enabled' : '⚠️ Disabled'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Phone Calls</span>
                <span className={`status-badge ${config?.features?.phoneCall ? 'success' : 'warning'}`}>
                  {config?.features?.phoneCall ? '✅ Available' : '⚠️ Unavailable'}
                </span>
              </div>
            </div>
            
            <div className="card-actions">
              <button 
                onClick={testConnection} 
                className="btn btn-secondary"
                disabled={testing}
              >
                {testing ? 'Testing...' : '🔍 Test Connection'}
              </button>
              <button onClick={loadConfig} className="btn btn-ghost">
                🔄 Refresh
              </button>
            </div>

            {testResult && (
              <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                {testResult.success ? '✅' : '❌'} {testResult.message}
              </div>
            )}
          </div>
        </div>

        {/* Configuration Details */}
        <div className="admin-card">
          <div className="card-header">
            <h3>⚙️ Configuration</h3>
          </div>
          <div className="card-body">
            <div className="config-list">
              <div className="config-item">
                <span className="config-key">VAPI Public Key</span>
                <span className="config-value">
                  {config?.publicKey ? '••••••••' + config.publicKey.slice(-8) : 'Not set'}
                </span>
              </div>
              <div className="config-item">
                <span className="config-key">Assistant ID</span>
                <span className="config-value">
                  {config?.assistantId || 'Dynamic (per session)'}
                </span>
              </div>
              <div className="config-item">
                <span className="config-key">Phone Number</span>
                <span className="config-value">
                  {config?.phoneNumber || 'Not configured'}
                </span>
              </div>
            </div>

            <div className="config-note">
              <span className="note-icon">💡</span>
              <p>
                Voice configuration is managed through environment variables. 
                Update your <code>.env</code> file to change these settings.
              </p>
            </div>
          </div>
        </div>

        {/* Web Voice Mode Explanation */}
        <div className="admin-card full-width">
          <div className="card-header">
            <h3>🎤 Web Voice Modes</h3>
          </div>
          <div className="card-body">
            <div className="mode-comparison">
              <div className="mode-card">
                <h4>🖥️ Browser Mode (Default)</h4>
                <p className="mode-status">
                  {!config?.vapiWebVoiceEnabled ? '✅ Currently Active' : '⚪ Disabled'}
                </p>
                <ul>
                  <li>Uses browser's built-in speech recognition</li>
                  <li>Sends text to your Claude backend</li>
                  <li>Uses browser's speech synthesis for responses</li>
                  <li><strong>Cost:</strong> Only Claude API usage</li>
                </ul>
              </div>
              <div className="mode-card">
                <h4>🌐 VAPI Mode</h4>
                <p className="mode-status">
                  {config?.vapiWebVoiceEnabled ? '✅ Currently Active' : '⚪ Disabled'}
                </p>
                <ul>
                  <li>Uses VAPI's cloud voice AI</li>
                  <li>Professional voice quality</li>
                  <li>Lower latency, natural interruptions</li>
                  <li><strong>Cost:</strong> VAPI pricing + LLM costs</li>
                </ul>
              </div>
            </div>
            
            <div className="config-note" style={{ marginTop: '16px' }}>
              <span className="note-icon">🔧</span>
              <p>
                To switch modes, set <code>VAPI_WEB_VOICE_ENABLED=true</code> or <code>false</code> in your <code>.env</code> file and restart the backend.
              </p>
            </div>
          </div>
        </div>

        {/* Required Environment Variables */}
        <div className="admin-card full-width">
          <div className="card-header">
            <h3>📋 Required Environment Variables</h3>
          </div>
          <div className="card-body">
            <div className="env-table">
              <table>
                <thead>
                  <tr>
                    <th>Variable</th>
                    <th>Description</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>VAPI_API_KEY</code></td>
                    <td>VAPI API key for server-side calls</td>
                    <td>
                      <span className={`status-dot ${config?.enabled ? 'green' : 'red'}`}></span>
                    </td>
                  </tr>
                  <tr>
                    <td><code>VAPI_PUBLIC_KEY</code></td>
                    <td>VAPI public key for web client (optional)</td>
                    <td>
                      <span className={`status-dot ${config?.publicKey ? 'green' : 'yellow'}`}></span>
                    </td>
                  </tr>
                  <tr>
                    <td><code>VAPI_ASSISTANT_ID</code></td>
                    <td>Pre-configured assistant ID (optional)</td>
                    <td>
                      <span className={`status-dot ${config?.assistantId ? 'green' : 'yellow'}`}></span>
                    </td>
                  </tr>
                  <tr>
                    <td><code>VAPI_WEB_VOICE_ENABLED</code></td>
                    <td>Enable VAPI for web voice (true/false)</td>
                    <td>
                      <span className={`status-dot ${config?.vapiWebVoiceEnabled ? 'green' : 'yellow'}`}></span>
                    </td>
                  </tr>
                  <tr>
                    <td><code>TWILIO_PHONE_NUMBER</code></td>
                    <td>Twilio phone number for inbound calls</td>
                    <td>
                      <span className={`status-dot ${config?.features?.phoneCall ? 'green' : 'yellow'}`}></span>
                    </td>
                  </tr>
                  <tr>
                    <td><code>VAPI_PHONE_NUMBER</code></td>
                    <td>VAPI phone number for SIP transfer</td>
                    <td>
                      <span className={`status-dot ${config?.features?.phoneCall ? 'green' : 'yellow'}`}></span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Voice Personalities */}
        <div className="admin-card">
          <div className="card-header">
            <h3>🎭 Voice Personalities</h3>
          </div>
          <div className="card-body">
            <div className="personality-list">
              <div className="personality-item">
                <span className="personality-icon">😊</span>
                <div className="personality-info">
                  <strong>Friendly</strong>
                  <p>Warm, encouraging, supportive</p>
                </div>
              </div>
              <div className="personality-item">
                <span className="personality-icon">👔</span>
                <div className="personality-info">
                  <strong>Strict</strong>
                  <p>Professional, direct, challenging</p>
                </div>
              </div>
              <div className="personality-item">
                <span className="personality-icon">🔬</span>
                <div className="personality-info">
                  <strong>Technical</strong>
                  <p>Detail-oriented, analytical</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Voice Usage Stats */}
        <div className="admin-card">
          <div className="card-header">
            <h3>📊 Voice Usage</h3>
          </div>
          <div className="card-body">
            <div className="stats-grid">
              <div className="stat-box">
                <span className="stat-value">--</span>
                <span className="stat-label">Voice Sessions</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">--</span>
                <span className="stat-label">Phone Sessions</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">--</span>
                <span className="stat-label">Total Minutes</span>
              </div>
            </div>
            <p className="stats-note">Voice usage statistics coming soon</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVoiceSettings;